import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Edit3, Layers, Trash2, CheckCircle2,
  Archive, Copy, Grid3x3, X, ChevronDown, ChevronRight,
} from "lucide-react";
import { venuesApi, TEMPLATE_TYPES } from "../../lib/events";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const cls = status === "published"
    ? "bg-[var(--gt-ink)] text-white"
    : status === "archived"
      ? "bg-[var(--gt-bg-surface)] text-[var(--gt-ink-3)] border border-[var(--gt-line)]"
      : "bg-white text-[var(--gt-ink)] border border-[var(--gt-line)]";
  return (
    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-sm ${cls}`} data-testid={`template-status-${status}`}>
      {status}
    </span>
  );
}

/* --------------- Template detail (sections + seats) --------------- */
function TemplateDetail({ template, onClose, onChange }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  // section form
  const [secForm, setSecForm] = useState({ name: "", capacity: 50, display_order: 0 });
  // seat gen form
  const [gen, setGen] = useState({ section_id: "", row_start: "A", row_end: "E", seats_per_row: 20, seat_prefix: "", is_accessible: false });

  const load = () => venuesApi.getTemplate(template.id).then((d) => { setDetail(d); if (d.sections[0]) setGen((g) => ({ ...g, section_id: d.sections[0].id })); });
  useEffect(() => { load(); }, [template.id]);

  const isReserved = template.template_type === "reserved_seating";

  const addSection = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await venuesApi.addSection(template.id, { ...secForm, capacity: parseInt(secForm.capacity, 10), display_order: parseInt(secForm.display_order, 10) });
      toast.success("Section added");
      setSecForm({ name: "", capacity: 50, display_order: 0 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add section.");
    } finally { setBusy(false); }
  };

  const rmSection = async (sid) => {
    if (!window.confirm("Delete this section and all its seats?")) return;
    await venuesApi.removeSection(template.id, sid);
    toast.success("Section removed");
    load();
  };

  const runGenerator = async (e) => {
    e.preventDefault();
    if (!gen.section_id) return toast.error("Pick a section first.");
    setBusy(true);
    try {
      const res = await venuesApi.generateSeats(template.id, { ...gen, seats_per_row: parseInt(gen.seats_per_row, 10) });
      toast.success(`Generated ${res.generated} seat${res.generated === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate seats.");
    } finally { setBusy(false); }
  };

  const publish = async () => {
    try {
      await venuesApi.publishTemplate(template.id);
      toast.success("Template published");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not publish template.");
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this template?")) return;
    await venuesApi.archiveTemplate(template.id);
    toast.success("Template archived");
    onChange();
    onClose();
  };

  const clone = async () => {
    const c = await venuesApi.cloneTemplate(template.id);
    toast.success(`Cloned as v${c.version}`);
    onChange();
  };

  const seatsByRow = useMemo(() => {
    if (!detail) return {};
    const map = {};
    for (const s of detail.seats) {
      const key = `${s.section_id}__${s.row_label}`;
      (map[key] ||= []).push(s);
    }
    return map;
  }, [detail]);

  if (!detail) return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;

  return (
    <div className="border border-[var(--gt-line)] rounded-sm bg-white" data-testid={`template-detail-${template.id}`}>
      <div className="flex items-center justify-between p-4 border-b border-[var(--gt-line)]">
        <div className="flex items-center gap-3">
          <StatusBadge status={template.status} />
          <div>
            <div className="font-heading font-bold">{template.template_name}</div>
            <div className="text-xs text-[var(--gt-ink-3)]">
              {TEMPLATE_TYPES.find((t) => t.value === template.template_type)?.label} · capacity {template.capacity} · v{template.version}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {template.status === "draft" && (
            <button onClick={publish} className="gt-btn-secondary text-xs" data-testid={`template-publish-${template.id}`}>
              <CheckCircle2 size={12} /> Publish
            </button>
          )}
          {template.status !== "archived" && (template.template_type === "reserved_seating" || template.template_type === "table_seating") && (
            <>
              <Link
                to={`/dashboard/venues/${template.venue_id}/templates/${template.id}/designer`}
                className="gt-btn-primary text-xs"
                data-testid={`template-canvas-designer-${template.id}`}
              >
                <Grid3x3 size={12} /> Canvas designer
              </Link>
              <Link
                to={`/dashboard/venues/${template.venue_id}/templates/${template.id}/layout`}
                className="gt-btn-ghost text-xs"
                data-testid={`template-designer-${template.id}`}
              >
                Preset layout
              </Link>
            </>
          )}
          {template.status !== "archived" && (
            <button onClick={clone} className="gt-btn-ghost text-xs" data-testid={`template-clone-${template.id}`}>
              <Copy size={12} /> Clone
            </button>
          )}
          {template.status !== "archived" && (
            <button onClick={archive} className="gt-btn-ghost text-xs" data-testid={`template-archive-${template.id}`}>
              <Archive size={12} /> Archive
            </button>
          )}
          <button onClick={onClose} className="gt-btn-ghost text-xs">
            <X size={12} />
          </button>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sections */}
        <div>
          <div className="gt-label mb-3">Sections</div>
          {detail.sections.length === 0 && (
            <div className="text-sm text-[var(--gt-ink-3)] mb-3">No sections yet. Add one to start allocating seats.</div>
          )}
          <div className="flex flex-col gap-2 mb-4">
            {detail.sections.map((s) => {
              const seatCount = detail.seats.filter((x) => x.section_id === s.id).length;
              return (
                <div key={s.id} className="border border-[var(--gt-line)] rounded-sm p-3 flex items-center justify-between" data-testid={`section-row-${s.id}`}>
                  <div>
                    <div className="text-sm font-bold">{s.name}</div>
                    <div className="text-xs text-[var(--gt-ink-3)]">{seatCount} / {s.capacity} seats · order {s.display_order}</div>
                  </div>
                  {template.status !== "archived" && (
                    <button onClick={() => rmSection(s.id)} className="text-xs text-[var(--gt-ink-3)] hover:text-[var(--gt-accent)]" data-testid={`section-remove-${s.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {template.status !== "archived" && (
            <form onSubmit={addSection} className="border border-dashed border-[var(--gt-line)] rounded-sm p-3 flex flex-col gap-2">
              <div className="gt-label">Add section</div>
              <input required placeholder="Section name (e.g. VIP)" className="gt-input" value={secForm.name} onChange={(e) => setSecForm({ ...secForm, name: e.target.value })} data-testid="section-form-name" />
              <div className="grid grid-cols-2 gap-2">
                <input required type="number" min="1" placeholder="Capacity" className="gt-input" value={secForm.capacity} onChange={(e) => setSecForm({ ...secForm, capacity: e.target.value })} data-testid="section-form-capacity" />
                <input type="number" min="0" placeholder="Display order" className="gt-input" value={secForm.display_order} onChange={(e) => setSecForm({ ...secForm, display_order: e.target.value })} />
              </div>
              <button type="submit" disabled={busy} className="gt-btn-secondary text-sm" data-testid="section-form-submit">
                <Plus size={14} /> Add section
              </button>
            </form>
          )}
        </div>

        {/* Seats + generator */}
        <div>
          {isReserved && (
            <>
              <div className="gt-label mb-3">Bulk seat generator</div>
              {detail.sections.length === 0 ? (
                <div className="text-sm text-[var(--gt-ink-3)] mb-4">Add a section first before generating seats.</div>
              ) : (
                template.status !== "archived" && (
                  <form onSubmit={runGenerator} className="border border-[var(--gt-line)] rounded-sm p-3 flex flex-col gap-2 mb-6">
                    <select className="gt-input" value={gen.section_id} onChange={(e) => setGen({ ...gen, section_id: e.target.value })} data-testid="seat-gen-section">
                      {detail.sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs">Row start</label>
                        <input required className="gt-input" value={gen.row_start} onChange={(e) => setGen({ ...gen, row_start: e.target.value })} data-testid="seat-gen-row-start" />
                      </div>
                      <div>
                        <label className="text-xs">Row end</label>
                        <input required className="gt-input" value={gen.row_end} onChange={(e) => setGen({ ...gen, row_end: e.target.value })} data-testid="seat-gen-row-end" />
                      </div>
                      <div>
                        <label className="text-xs">Seats/row</label>
                        <input required type="number" min="1" className="gt-input" value={gen.seats_per_row} onChange={(e) => setGen({ ...gen, seats_per_row: e.target.value })} data-testid="seat-gen-seats-per-row" />
                      </div>
                    </div>
                    <input placeholder="Optional seat prefix (e.g. V)" className="gt-input" value={gen.seat_prefix} onChange={(e) => setGen({ ...gen, seat_prefix: e.target.value })} data-testid="seat-gen-prefix" />
                    <label className="text-xs inline-flex items-center gap-2">
                      <input type="checkbox" checked={gen.is_accessible} onChange={(e) => setGen({ ...gen, is_accessible: e.target.checked })} />
                      Mark as accessible seats
                    </label>
                    <button type="submit" disabled={busy} className="gt-btn-primary text-sm" data-testid="seat-gen-submit">
                      <Grid3x3 size={14} /> Generate seats
                    </button>
                  </form>
                )
              )}

              <div className="gt-label mb-2">Seats ({detail.seats.length})</div>
              <div className="max-h-96 overflow-auto border border-[var(--gt-line)] rounded-sm p-3 bg-[var(--gt-bg-surface)]" data-testid="template-seat-grid">
                {detail.sections.map((sec) => {
                  const rows = {};
                  for (const st of detail.seats.filter((x) => x.section_id === sec.id)) {
                    (rows[st.row_label] ||= []).push(st);
                  }
                  const rowKeys = Object.keys(rows).sort();
                  if (rowKeys.length === 0) return null;
                  return (
                    <div key={sec.id} className="mb-4">
                      <div className="text-xs font-bold mb-2">{sec.name}</div>
                      <div className="flex flex-col gap-1">
                        {rowKeys.map((r) => (
                          <div key={r} className="flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] font-mono w-6 text-[var(--gt-ink-3)]">{r}</span>
                            {rows[r].sort((a, b) => a.seat_number - b.seat_number).map((st) => (
                              <span
                                key={st.id}
                                className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${st.status === "disabled" ? "bg-[var(--gt-bg-surface)] text-[var(--gt-ink-3)] border-[var(--gt-line)] line-through" : "bg-white border-[var(--gt-line)]"} ${st.is_accessible ? "ring-1 ring-[var(--gt-accent)]/40" : ""}`}
                                title={st.is_accessible ? "Accessible" : ""}
                                data-testid={`template-seat-${st.seat_label}`}
                              >
                                {st.seat_label}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {!isReserved && (
            <div className="text-sm text-[var(--gt-ink-2)] p-6 border border-dashed border-[var(--gt-line)] rounded-sm">
              This template is <b>General Admission</b>. Attach it to an event to
              let the event's ticket types drive capacity.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------- Venue detail page --------------- */
export default function VenueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [openTemplateId, setOpenTemplateId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTpl, setNewTpl] = useState({ template_name: "", template_type: "general_admission", capacity: 100, is_default: false });
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [v, tpls] = await Promise.all([venuesApi.get(id), venuesApi.listTemplates(id, true)]);
      setVenue(v);
      setTemplates(tpls);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load venue.");
    }
  };
  useEffect(() => { load(); }, [id]);

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      const t = await venuesApi.createTemplate(id, { ...newTpl, capacity: parseInt(newTpl.capacity, 10) });
      toast.success("Template created");
      setShowNew(false);
      setNewTpl({ template_name: "", template_type: "general_admission", capacity: 100, is_default: false });
      setOpenTemplateId(t.id);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create template.");
    }
  };

  if (error) return <div className="p-10 text-[var(--gt-accent)]">{error}</div>;
  if (!venue) return <div className="p-10"><Loader2 size={18} className="animate-spin" /></div>;

  return (
    <section className="p-6 sm:p-10" data-testid="venue-detail-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/dashboard/venues" className="gt-btn-ghost text-xs mb-6 inline-flex" data-testid="venue-detail-back">
          <ArrowLeft size={13} /> Back to venues
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="gt-label mb-2">Venue</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-2" data-testid="venue-detail-name">
              {venue.name}
            </h2>
            <div className="text-sm text-[var(--gt-ink-2)]">
              {[venue.address, venue.suburb, venue.state, venue.postcode].filter(Boolean).join(", ")} · Capacity {venue.capacity}
            </div>
          </div>
          <button onClick={() => navigate(`/dashboard/venues/${id}/edit`)} className="gt-btn-secondary" data-testid="venue-detail-edit">
            <Edit3 size={14} /> Edit venue
          </button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="gt-label">Templates</div>
            <h3 className="font-heading text-xl font-bold tracking-tight">Reusable layouts</h3>
          </div>
          <button onClick={() => setShowNew((v) => !v)} className="gt-btn-primary text-sm" data-testid="template-create-btn">
            <Plus size={14} /> New template
          </button>
        </div>

        {showNew && (
          <form onSubmit={submitNew} className="border border-[var(--gt-line)] rounded-sm p-4 mb-4 bg-white flex flex-col gap-3" data-testid="template-new-form">
            <input required placeholder="Template name (e.g. Main Hall — Full)" className="gt-input" value={newTpl.template_name} onChange={(e) => setNewTpl({ ...newTpl, template_name: e.target.value })} data-testid="template-new-name" />
            <div className="grid grid-cols-2 gap-3">
              <select className="gt-input" value={newTpl.template_type} onChange={(e) => setNewTpl({ ...newTpl, template_type: e.target.value })} data-testid="template-new-type">
                {TEMPLATE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input required type="number" min="1" max={venue.capacity} placeholder="Capacity" className="gt-input" value={newTpl.capacity} onChange={(e) => setNewTpl({ ...newTpl, capacity: e.target.value })} data-testid="template-new-capacity" />
            </div>
            <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={newTpl.is_default} onChange={(e) => setNewTpl({ ...newTpl, is_default: e.target.checked })} /> Set as default template for this venue</label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="gt-btn-ghost text-sm">Cancel</button>
              <button type="submit" className="gt-btn-primary text-sm" data-testid="template-new-submit">Create template</button>
            </div>
          </form>
        )}

        {templates.length === 0 && !showNew && (
          <div className="border border-[var(--gt-line)] rounded-sm p-8 text-center bg-white" data-testid="templates-empty">
            <Layers size={26} className="mx-auto mb-3 text-[var(--gt-ink-3)]" />
            <p className="text-sm text-[var(--gt-ink-2)]">No templates yet. Create your first layout.</p>
          </div>
        )}

        <div className="flex flex-col gap-3" data-testid="templates-list">
          {templates.map((t) => (
            <div key={t.id}>
              <button
                type="button"
                onClick={() => setOpenTemplateId((cur) => (cur === t.id ? null : t.id))}
                className="w-full text-left border border-[var(--gt-line)] rounded-sm bg-white px-4 py-3 flex items-center justify-between hover:border-[var(--gt-ink)]"
                data-testid={`template-row-${t.id}`}
              >
                <div className="flex items-center gap-3">
                  {openTemplateId === t.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <StatusBadge status={t.status} />
                  <div>
                    <div className="font-bold text-sm">{t.template_name} {t.is_default && <span className="text-[10px] text-[var(--gt-accent)] ml-1">DEFAULT</span>}</div>
                    <div className="text-xs text-[var(--gt-ink-3)]">
                      {TEMPLATE_TYPES.find((x) => x.value === t.template_type)?.label} · capacity {t.capacity} · v{t.version}
                    </div>
                  </div>
                </div>
              </button>
              {openTemplateId === t.id && (
                <div className="mt-2">
                  <TemplateDetail template={t} onClose={() => setOpenTemplateId(null)} onChange={load} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
